import React from 'react';
import {
  Box,
  Avatar,
  Container,
  Paper,
  Typography,
  Divider,
  Grid,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Header } from '../../components/Header/Headers';
import { RootState } from '../../store';
import { AuthUser } from '../../store/slices/authSlice';

const formatJoinDate = (value?: string) => {
  if (!value) {
    return 'January 15, 2024';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'AU';
  }
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
};

const formatRole = (value?: string) => {
  if (!value) {
    return 'System Administrator';
  }

  if (value === 'ADMIN') {
    return 'System Administrator';
  }

  return value;
};

const infoLabelSx = {
  color: '#424242',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '19.92px',
};

const infoValueSx = {
  color: '#1A1A1A',
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '24px',
};

const infoSectionTitleSx = {
  color: '#1A1A1A',
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '32px',
};

const UserProfile: React.FC = () => {
  const storedUser = React.useMemo(() => {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) {
      return null;
    }

    try {
      return JSON.parse(rawUser) as AuthUser;
    } catch {
      return null;
    }
  }, []);

  const authUser = useSelector((state: RootState) => state.auth.user);
  const user = authUser || storedUser;

  const name = user?.name || 'Admin User';
  const role = formatRole(user?.role);
  const email = user?.email || 'admin@craksha.com';
  const phone = user?.phone || '+91 98765 43210';
  const joinDate = formatJoinDate(user?.joinDate);

  return (
    <>
      <Header title="My Profile" breadcrumb="Admin Console / My Profile" />
      <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5', py: 4 }}>
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid #E0E0E0',
              boxShadow: '0 1px 3px rgba(16, 24, 40, 0.12), 0 1px 2px rgba(16, 24, 40, 0.08)',
              p: { xs: 3, sm: 4 },
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2.5, sm: 3.5 }, flexWrap: 'wrap' }}>
              <Avatar
                sx={{
                  width: 122,
                  height: 122,
                  bgcolor: '#2E7D32',
                  color: '#FFF',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '44px',
                  fontWeight: 700,
                  lineHeight: '53px',
                }}
              >
                {getInitials(name)}
              </Avatar>

              <Box>
                <Typography
                  component="h1"
                  sx={{
                    color: '#1A1A1A',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '34px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '41.99px',
                    mb: 1,
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  sx={{
                    color: '#424242',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '24px',
                  }}
                >
                  {role}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 4, lg: 3.3 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #E0E0E0',
                  boxShadow: '0 1px 3px rgba(16, 24, 40, 0.12), 0 1px 2px rgba(16, 24, 40, 0.08)',
                  p: 3,
                }}
              >
                <Typography sx={{ ...infoSectionTitleSx, mb: 3.5 }}>
                  Personal Information
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pb: 2.75 }}>
                  <EmailIcon sx={{ color: '#2E7D32', fontSize: 24, mt: 0.2 }} />
                  <Box>
                    <Typography sx={infoLabelSx}>Email Address</Typography>
                    <Typography sx={infoValueSx}>{email}</Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: '#EAEAEA', mb: 2.75 }} />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pb: 2.75 }}>
                  <PhoneIcon sx={{ color: '#2E7D32', fontSize: 24, mt: 0.2 }} />
                  <Box>
                    <Typography sx={infoLabelSx}>Phone Number</Typography>
                    <Typography sx={infoValueSx}>{phone}</Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: '#EAEAEA' }} />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4, lg: 3.7 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #E0E0E0',
                  boxShadow: '0 1px 3px rgba(16, 24, 40, 0.12), 0 1px 2px rgba(16, 24, 40, 0.08)',
                  p: 3,
                  minHeight: 213,
                }}
              >
                <Typography sx={{ ...infoSectionTitleSx, mb: 3.5 }}>
                  Employment Information
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CalendarIcon sx={{ color: '#2E7D32', fontSize: 24, mt: 0.2 }} />
                  <Box>
                    <Typography sx={infoLabelSx}>Join Date</Typography>
                    <Typography sx={infoValueSx}>{joinDate}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default UserProfile;